import { useQuery } from '@tanstack/react-query'
import { Capacitor } from '@capacitor/core'
import { WeatherKitNative } from '../lib/native/weather-kit'

/**
 * Full-fidelity weather conditions — one token per Apple WeatherKit `WeatherCondition`
 * case (see the `map()` switch in `ios/App/App/WeatherKitPlugin.swift`). Day/night
 * variants exist only for clear / mostly-clear / partly-cloudy.
 */
export type WeatherCondition =
  | 'clear-day'
  | 'clear-night'
  | 'mostly-clear-day'
  | 'mostly-clear-night'
  | 'partly-cloudy-day'
  | 'partly-cloudy-night'
  | 'mostly-cloudy'
  | 'cloudy'
  | 'hot'
  | 'frigid'
  | 'fog'
  | 'haze'
  | 'smoke'
  | 'breezy'
  | 'windy'
  | 'blowing-dust'
  | 'drizzle'
  | 'rain'
  | 'heavy-rain'
  | 'sun-showers'
  | 'isolated-thunderstorms'
  | 'scattered-thunderstorms'
  | 'thunderstorm'
  | 'strong-storms'
  | 'hail'
  | 'sleet'
  | 'freezing-drizzle'
  | 'freezing-rain'
  | 'wintry-mix'
  | 'snow'
  | 'heavy-snow'
  | 'flurries'
  | 'sun-flurries'
  | 'blizzard'
  | 'blowing-snow'
  | 'hurricane'
  | 'tropical-storm'

/** Emoji for each WeatherKit condition. */
export const weatherEmojis: Record<WeatherCondition, string> = {
  'clear-day': '☀️',
  'clear-night': '🌙',
  'mostly-clear-day': '🌤️',
  'mostly-clear-night': '🌙',
  'partly-cloudy-day': '⛅',
  'partly-cloudy-night': '🌥️',
  'mostly-cloudy': '🌥️',
  cloudy: '☁️',
  hot: '🥵',
  frigid: '🥶',
  fog: '🌫️',
  haze: '🌫️',
  smoke: '🌁',
  breezy: '🍃',
  windy: '💨',
  'blowing-dust': '🌬️',
  drizzle: '🌦️',
  rain: '🌧️',
  'heavy-rain': '🌧️',
  'sun-showers': '🌦️',
  'isolated-thunderstorms': '🌩️',
  'scattered-thunderstorms': '⛈️',
  thunderstorm: '⛈️',
  'strong-storms': '⛈️',
  hail: '🌨️',
  sleet: '🌨️',
  'freezing-drizzle': '🌨️',
  'freezing-rain': '🌨️',
  'wintry-mix': '🌨️',
  snow: '❄️',
  'heavy-snow': '❄️',
  flurries: '🌨️',
  'sun-flurries': '🌨️',
  blizzard: '❄️',
  'blowing-snow': '🌨️',
  hurricane: '🌀',
  'tropical-storm': '🌀',
}

/** Human-readable label for each WeatherKit condition. */
export const conditionLabels: Record<WeatherCondition, string> = {
  'clear-day': 'Clear',
  'clear-night': 'Clear',
  'mostly-clear-day': 'Mostly clear',
  'mostly-clear-night': 'Mostly clear',
  'partly-cloudy-day': 'Partly cloudy',
  'partly-cloudy-night': 'Partly cloudy',
  'mostly-cloudy': 'Mostly cloudy',
  cloudy: 'Cloudy',
  hot: 'Hot',
  frigid: 'Frigid',
  fog: 'Fog',
  haze: 'Haze',
  smoke: 'Smoke',
  breezy: 'Breezy',
  windy: 'Windy',
  'blowing-dust': 'Blowing dust',
  drizzle: 'Drizzle',
  rain: 'Rain',
  'heavy-rain': 'Heavy rain',
  'sun-showers': 'Sun showers',
  'isolated-thunderstorms': 'Isolated thunderstorms',
  'scattered-thunderstorms': 'Scattered thunderstorms',
  thunderstorm: 'Thunderstorms',
  'strong-storms': 'Strong storms',
  hail: 'Hail',
  sleet: 'Sleet',
  'freezing-drizzle': 'Freezing drizzle',
  'freezing-rain': 'Freezing rain',
  'wintry-mix': 'Wintry mix',
  snow: 'Snow',
  'heavy-snow': 'Heavy snow',
  flurries: 'Flurries',
  'sun-flurries': 'Sun flurries',
  blizzard: 'Blizzard',
  'blowing-snow': 'Blowing snow',
  hurricane: 'Hurricane',
  'tropical-storm': 'Tropical storm',
}

export interface WeatherData {
  time: Date
  temperature: number
  apparentTemperature: number
  isDay: boolean
  weatherCode: number
  weatherCondition: WeatherCondition
  cloudCover: number
  precipitation: number
  rain: number
  showers: number
  snowfall: number
  windSpeed: number
  windDirection: number
  windGusts: number
  /** UV index (0+); null when the source doesn't provide it. */
  uvIndex: number | null
}

/** Map a UV index value to its standard EPA exposure band. */
export function uvIndexBand(value: number): string {
  if (value <= 2) return 'Low'
  if (value <= 5) return 'Moderate'
  if (value <= 7) return 'High'
  if (value <= 10) return 'Very High'
  return 'Extreme'
}

interface UseWeatherOptions {
  latitude?: number
  longitude?: number
  enabled?: boolean
}

const isIOS = Capacitor.getPlatform() === 'ios'

/**
 * Map a WMO weather interpretation code (open-meteo) to our WeatherKit-style
 * condition token. Day/night only differentiates the clear/partly-cloudy cases.
 */
function wmoToCondition(code: number, isDay: boolean): WeatherCondition {
  switch (code) {
    case 0:
      return isDay ? 'clear-day' : 'clear-night'
    case 1:
      return isDay ? 'mostly-clear-day' : 'mostly-clear-night'
    case 2:
      return isDay ? 'partly-cloudy-day' : 'partly-cloudy-night'
    case 3:
      return 'cloudy'
    case 45:
    case 48:
      return 'fog'
    case 51:
    case 53:
    case 55:
      return 'drizzle'
    case 56:
    case 57:
      return 'freezing-drizzle'
    case 61:
    case 63:
      return 'rain'
    case 65:
      return 'heavy-rain'
    case 66:
    case 67:
      return 'freezing-rain'
    case 71:
    case 73:
      return 'snow'
    case 75:
      return 'heavy-snow'
    case 77:
      return 'flurries'
    case 80:
    case 81:
      return 'rain'
    case 82:
      return 'heavy-rain'
    case 85:
    case 86:
      return 'snow'
    case 95:
      return 'thunderstorm'
    case 96:
    case 99:
      return 'strong-storms'
    default:
      return isDay ? 'clear-day' : 'clear-night'
  }
}

interface OpenMeteoCurrent {
  time: string
  temperature_2m: number
  apparent_temperature: number
  weather_code: number
  is_day: number
  cloud_cover: number
  precipitation: number
  wind_speed_10m: number
  wind_direction_10m: number
  wind_gusts_10m: number
  uv_index: number
}

async function fetchOpenMeteoWeather(latitude: number, longitude: number): Promise<WeatherData> {
  const params = new URLSearchParams({
    latitude: String(latitude),
    longitude: String(longitude),
    current: [
      'temperature_2m',
      'apparent_temperature',
      'weather_code',
      'is_day',
      'cloud_cover',
      'precipitation',
      'wind_speed_10m',
      'wind_direction_10m',
      'wind_gusts_10m',
      'uv_index',
    ].join(','),
  })
  const res = await fetch(`https://api.open-meteo.com/v1/forecast?${params.toString()}`)
  if (!res.ok) throw new Error(`open-meteo request failed: ${res.status}`)
  const json = (await res.json()) as { current: OpenMeteoCurrent }
  const c = json.current
  const isDay = c.is_day === 1
  return {
    time: new Date(c.time),
    temperature: c.temperature_2m,
    apparentTemperature: c.apparent_temperature,
    isDay,
    weatherCode: c.weather_code,
    weatherCondition: wmoToCondition(c.weather_code, isDay),
    cloudCover: c.cloud_cover,
    precipitation: c.precipitation,
    rain: 0,
    showers: 0,
    snowfall: 0,
    windSpeed: c.wind_speed_10m,
    windDirection: c.wind_direction_10m,
    windGusts: c.wind_gusts_10m,
    uvIndex: c.uv_index,
  }
}

/**
 * Current weather for a coordinate. iOS fetches Apple WeatherKit natively
 * on-device; web/Android fall back to the open-meteo public API (free, no key,
 * CORS-enabled) so the weather card still renders off-iOS.
 */
export function useWeather({ latitude, longitude, enabled = true }: UseWeatherOptions) {
  return useQuery({
    queryKey: ['weather', latitude, longitude],
    queryFn: async (): Promise<WeatherData> => {
      if (isIOS) {
        const raw = await WeatherKitNative.getCurrentWeather({
          latitude: latitude!,
          longitude: longitude!,
        })
        return {
          time: new Date(raw.time),
          temperature: raw.temperature,
          apparentTemperature: raw.apparentTemperature,
          isDay: raw.isDay,
          weatherCode: 0,
          weatherCondition: raw.weatherCondition as WeatherCondition,
          cloudCover: raw.cloudCover,
          precipitation: raw.precipitation,
          rain: 0,
          showers: 0,
          snowfall: 0,
          windSpeed: raw.windSpeed,
          windDirection: raw.windDirection,
          windGusts: raw.windGusts,
          uvIndex: raw.uvIndex,
        }
      }
      return fetchOpenMeteoWeather(latitude!, longitude!)
    },
    enabled: enabled && !!latitude && !!longitude,
    staleTime: 2 * 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
    retry: false,
  })
}
